/**
 * View-mode click feedback: scale pulse around the element center (Konva Tween).
 * Table `Group`s use a fixed center offset from authoring; other nodes use `getClientRect({ skipTransform: true })`
 * so BAR stools (outside logical height) do not shift during the pulse (`Group` has no `getSelfRect` in Konva).
 */

export interface FloorPlanViewClickPulseOptions {
  readonly durationMs: number;
  /** Peak scale (e.g. 1.05 = +5%). */
  readonly peakScale: number;
  /** Mid pulse scale (e.g. 0.95 = −5% vs original). */
  readonly valleyScale: number;
  /**
   * Called once when the three-phase pulse finishes normally (scale back to 1, geometry restored).
   * Not called when the returned cancel runs or tweens are destroyed early.
   */
  readonly onComplete?: () => void;
}

export interface FloorPlanViewClickPulsePrep {
  readonly restoreGeometry: () => void;
}

/**
 * Local-space center for scaling: union of painted children in the node's own coords.
 * Konva **`Group` has no `getSelfRect`** — use **`getClientRect({ skipTransform: true })`** (Container API)
 * so BAR stools below the logical bbox are included (fix: pulse used to fall back to `height/2` and slid the bar).
 */
export function pulseScaleOriginLocal(node: any, width: number, height: number): { cx: number; cy: number } {
  try {
    if (typeof node.getClientRect === 'function') {
      const r = node.getClientRect({ skipTransform: true });
      if (
        r &&
        r.width > 0 &&
        r.height > 0 &&
        Number.isFinite(r.x) &&
        Number.isFinite(r.y)
      ) {
        return { cx: r.x + r.width / 2, cy: r.y + r.height / 2 };
      }
    }
  } catch {
    /* use fallback below */
  }
  return { cx: width / 2, cy: height / 2 };
}

/**
 * Sets `offsetX/offsetY` to `(cx, cy)` and shifts `node.x/y` so the **world** position of local `(cx, cy)` stays the same.
 * `getAbsolutePosition` / `setAbsolutePosition` do **not** preserve that point (they follow a different anchor), which made
 * BAR/groups look like the top-left corner jumped to the former centre during the pulse.
 */
export function alignNodeOffsetToLocalPointWithoutMovingWorld(node: any, cx: number, cy: number): void {
  if (typeof node.getAbsoluteTransform !== 'function') {
    node.offsetX(cx);
    node.offsetY(cy);
    return;
  }

  const absBefore = node.getAbsoluteTransform();
  const worldAt = absBefore.point({ x: cx, y: cy });

  node.offsetX(cx);
  node.offsetY(cy);

  const absAfter = node.getAbsoluteTransform();
  const worldAfter = absAfter.point({ x: cx, y: cy });

  const dx = worldAt.x - worldAfter.x;
  const dy = worldAt.y - worldAfter.y;

  const parent = typeof node.getParent === 'function' ? node.getParent() : null;
  if (!parent || typeof parent.getAbsoluteTransform !== 'function') {
    return;
  }

  const pAbs = parent.getAbsoluteTransform();
  const oW = pAbs.point({ x: 0, y: 0 });
  const e1W = pAbs.point({ x: 1, y: 0 });
  const e2W = pAbs.point({ x: 0, y: 1 });
  const e1x = e1W.x - oW.x;
  const e1y = e1W.y - oW.y;
  const e2x = e2W.x - oW.x;
  const e2y = e2W.y - oW.y;

  const det = e1x * e2y - e1y * e2x;
  if (Math.abs(det) < 1e-10) {
    return;
  }

  const px = (dx * e2y - dy * e2x) / det;
  const py = (e1x * dy - e1y * dx) / det;

  node.x(node.x() + px);
  node.y(node.y() + py);
}

/**
 * Ensures scaling uses a stable center: table `Group` (offset = half logical size) only scales;
 * other nodes use {@link pulseScaleOriginLocal} so BAR / similar match the real painted bounds.
 */
export function prepareFloorPlanNodeForViewClickPulse(node: any): FloorPlanViewClickPulsePrep {
  const w = typeof node?.width === 'function' ? node.width() : 0;
  const h = typeof node?.height === 'function' ? node.height() : 0;
  if (!w || !h) {
    return { restoreGeometry: () => void 0 };
  }
  const ox = node.offsetX() || 0;
  const oy = node.offsetY() || 0;
  const halfW = w / 2;
  const halfH = h / 2;
  /** Matches {@link createFloorPlanTableNode} center-anchored groups only — not `getSelfRect` (chairs may extend past logical bbox). */
  const alreadyCentered = Math.abs(ox - halfW) < 1e-3 && Math.abs(oy - halfH) < 1e-3;

  if (alreadyCentered) {
    const absAnchor = node.getAbsolutePosition();
    return {
      restoreGeometry: () => {
        try {
          node.scaleX(1);
          node.scaleY(1);
          node.setAbsolutePosition(absAnchor);
        } catch {
          /* node may be destroyed */
        }
      },
    };
  }

  const { cx, cy } = pulseScaleOriginLocal(node, w, h);
  const origX = node.x();
  const origY = node.y();
  const origOx = node.offsetX() || 0;
  const origOy = node.offsetY() || 0;

  alignNodeOffsetToLocalPointWithoutMovingWorld(node, cx, cy);

  return {
    restoreGeometry: () => {
      try {
        node.scaleX(1);
        node.scaleY(1);
        node.offsetX(origOx);
        node.offsetY(origOy);
        node.x(origX);
        node.y(origY);
      } catch {
        /* node may be destroyed */
      }
    },
  };
}

/**
 * Runs one three-phase scale animation (peak → valley → 1). Returns cancel that stops tweens and restores geometry.
 * If the animation completes normally, {@link FloorPlanViewClickPulseOptions.onComplete} runs after restore.
 */
function runViewClickPulseOnce(K: any, node: any, options: FloorPlanViewClickPulseOptions): () => void {
  const tweens: any[] = [];
  let aborted = false;

  if (!K?.Tween || !node?.getLayer) {
    return () => void 0;
  }

  const prep = prepareFloorPlanNodeForViewClickPulse(node);
  try {
    node.scaleX(1);
    node.scaleY(1);
  } catch {
    return () => void 0;
  }

  const third = (options.durationMs / 1000) / 3;
  const easing = K.Easings.EaseInOut;

  const destroyAll = () => {
    aborted = true;
    for (const t of tweens) {
      try {
        t.destroy();
      } catch {
        /* ignore */
      }
    }
    tweens.length = 0;
    prep.restoreGeometry();
    try {
      node.getLayer()?.batchDraw();
    } catch {
      /* ignore */
    }
  };

  const t1 = new K.Tween({
    node,
    duration: third,
    scaleX: options.peakScale,
    scaleY: options.peakScale,
    easing,
    onFinish: () => {
      if (aborted) {
        return;
      }
      try {
        t1.destroy();
      } catch {
        /* ignore */
      }
      const i = tweens.indexOf(t1);
      if (i >= 0) {
        tweens.splice(i, 1);
      }
      const t2 = new K.Tween({
        node,
        duration: third,
        scaleX: options.valleyScale,
        scaleY: options.valleyScale,
        easing,
        onFinish: () => {
          if (aborted) {
            return;
          }
          try {
            t2.destroy();
          } catch {
            /* ignore */
          }
          const j = tweens.indexOf(t2);
          if (j >= 0) {
            tweens.splice(j, 1);
          }
          const t3 = new K.Tween({
            node,
            duration: third,
            scaleX: 1,
            scaleY: 1,
            easing,
            onFinish: () => {
              if (aborted) {
                return;
              }
              try {
                t3.destroy();
              } catch {
                /* ignore */
              }
              const k = tweens.indexOf(t3);
              if (k >= 0) {
                tweens.splice(k, 1);
              }
              prep.restoreGeometry();
              try {
                node.getLayer()?.batchDraw();
              } catch {
                /* ignore */
              }
              if (!aborted) {
                options.onComplete?.();
              }
            },
          });
          tweens.push(t3);
          t3.play();
        },
      });
      tweens.push(t2);
      t2.play();
    },
  });
  tweens.push(t1);
  t1.play();

  return destroyAll;
}

/**
 * Runs a three-phase scale animation (peak → valley → 1). Returns cancel that stops tweens and restores geometry.
 */
export function runFloorPlanViewClickPulse(K: any, node: any, options: FloorPlanViewClickPulseOptions): () => void {
  return runViewClickPulseOnce(K, node, options);
}

/**
 * Repeats the same three-phase pulse as {@link runFloorPlanViewClickPulse} in a loop (same total duration per cycle).
 * View mode only — host should cancel when leaving view or redrawing the scene.
 */
export function runFloorPlanViewAttentionPulseLoop(
  K: any,
  node: any,
  options: FloorPlanViewClickPulseOptions,
): () => void {
  let stopped = false;
  let cancelCurrent: (() => void) | null = null;

  const pulseOpts = (): FloorPlanViewClickPulseOptions => ({
    durationMs: options.durationMs,
    peakScale: options.peakScale,
    valleyScale: options.valleyScale,
    onComplete: () => {
      if (!stopped) {
        queueMicrotask(() => scheduleNext());
      }
    },
  });

  const scheduleNext = () => {
    if (stopped || !K?.Tween || !node?.getLayer) {
      return;
    }
    cancelCurrent = runViewClickPulseOnce(K, node, pulseOpts());
  };

  scheduleNext();

  return () => {
    stopped = true;
    cancelCurrent?.();
    cancelCurrent = null;
  };
}
